'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { ChevronDown } from 'lucide-react'

export interface TocItem {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  headings: TocItem[]
  variant?: 'desktop' | 'mobile'
}

function TocList({
  headings,
  activeId,
  onClickHeading,
}: {
  headings: TocItem[]
  activeId: string
  onClickHeading: (id: string) => void
}) {
  return (
    <ul className="flex flex-col gap-1 text-sm border-l border-gray-800">
      {headings.map((h) => (
        <li key={h.id}>
          <a
            href={`#${h.id}`}
            onClick={(e) => {
              e.preventDefault()
              onClickHeading(h.id)
            }}
            className={`block py-1 transition-colors duration-150 border-l-2 -ml-px ${
              h.level === 3 ? 'pl-6' : 'pl-4'
            } ${
              activeId === h.id
                ? 'border-white text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {h.text}
          </a>
        </li>
      ))}
    </ul>
  )
}

export default function TableOfContents({ headings, variant = 'desktop' }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('')
  const [mobileOpen, setMobileOpen] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (headings.length === 0) return

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

      if (visible.length > 0) {
        setActiveId(visible[0].target.id)
      }
    }

    observerRef.current = new IntersectionObserver(handleIntersect, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    const ids = new Set(headings.map((h) => h.id))
    document.querySelectorAll('h2[id], h3[id]').forEach((el) => {
      if (ids.has(el.id)) observerRef.current?.observe(el)
    })

    return () => observerRef.current?.disconnect()
  }, [headings])

  const handleClick = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    setActiveId(id)
    setMobileOpen(false)
  }, [])

  if (headings.length < 2) return null

  if (variant === 'mobile') {
    return (
      <nav aria-label="Table of contents" className="xl:hidden mb-8">
        <div className="border border-gray-800 rounded-xl overflow-hidden">
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-400 hover:text-white transition-colors"
          >
            <span className="uppercase tracking-wide text-xs">On this page</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${mobileOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {mobileOpen && (
            <div className="px-4 pb-4">
              <TocList headings={headings} activeId={activeId} onClickHeading={handleClick} />
            </div>
          )}
        </div>
      </nav>
    )
  }

  return (
    <nav aria-label="Table of contents" className="hidden xl:block">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        On this page
      </p>
      <TocList headings={headings} activeId={activeId} onClickHeading={handleClick} />
    </nav>
  )
}
